import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

type LanguageCode = 'es' | 'en' | 'pt';

export function LanguageSelector({
  menuPlacement = 'down',
}: {
  /** En el drawer mobile el control está abajo: el menú tiene que abrir hacia arriba. */
  menuPlacement?: 'up' | 'down';
}) {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const languages: Array<{ code: LanguageCode; label: string; name: string }> = [
    { code: 'es', label: 'ES', name: 'Español' },
    { code: 'en', label: 'EN', name: 'English' },
    { code: 'pt', label: 'PT', name: 'Português' },
  ];

  const current = languages.find((language) => language.code === i18n.language) ?? languages[0];

  useEffect(() => {
    function handlePointerDown(event: PointerEvent) {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }

    if (open) {
      document.addEventListener('pointerdown', handlePointerDown);
    }

    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, [open]);

  function changeLanguage(code: LanguageCode) {
    i18n.changeLanguage(code);
    localStorage.setItem('language', code);
    setOpen(false);
  }

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="inline-flex h-9 min-w-11 items-center justify-center rounded-md border border-gray-200 bg-white px-2 text-xs font-semibold text-gray-700 transition hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-teal-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
        aria-label="Select language"
        aria-expanded={open}
        aria-haspopup="listbox"
      >
        {current.label}
      </button>
      {open && (
        <div
          role="listbox"
          data-testid="language-menu"
          data-placement={menuPlacement}
          className={`absolute right-0 z-50 w-36 overflow-hidden rounded-md border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-800 ${
            menuPlacement === 'up' ? 'bottom-full mb-2' : 'mt-2'
          }`}
        >
          {languages.map((language) => (
            <button
              key={language.code}
              type="button"
              role="option"
              aria-selected={i18n.language === language.code}
              onClick={() => changeLanguage(language.code)}
              className={`flex w-full items-center justify-between px-3 py-2 text-left text-sm transition hover:bg-gray-100 dark:hover:bg-gray-700 ${
                i18n.language === language.code
                  ? 'bg-teal-50 text-teal-700 dark:bg-teal-900/30 dark:text-teal-200'
                  : 'text-gray-700 dark:text-gray-200'
              }`}
            >
              <span>{language.name}</span>
              <span className="text-xs font-semibold">{language.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
